import type {
  LocalTabEditStatus,
  TabEditKind,
  TabEditStatus,
  TabSong,
  UserRole
} from '../types/index.js';
import { isLocalOnlyTab } from './difficulty.js';
import { toPersianDigits } from './timeUtils.js';

// --- Limits (ADR 5.2, 6.1, Spec 4) ---

export const EDIT_SUGGEST_LIMIT_MAX = 20;
export const EDIT_SUGGEST_WINDOW_MS = 3_600_000;
export const EDIT_PENDING_MAX = 50;
export const TAB_SCORE_OPS_MAX = 2000;
export const EDIT_LIST_LIMIT = 500;
export const EDIT_NOTE_MAX_LENGTH = 300;
export const REJECT_REASON_MAX_LENGTH = 300;
export const TAB_TITLE_MAX_LENGTH = 200;
export const TAB_ARTIST_MAX_LENGTH = 200;
export const TAB_TUNING_MAX_LENGTH = 100;
export const LYRIC_CONTENT_MAX_LENGTH = 50_000;
export const SCORE_OPS_PER_EDIT_MAX = 200;
export const SCORE_TEMPO_MIN = 20;
export const SCORE_TEMPO_MAX = 400;
export const SCORE_FRET_MIN = 0;
export const SCORE_FRET_MAX = 24;
export const SCORE_CHORD_MAX_LENGTH = 24;

// --- Labels (Spec 7.1 C-24, C-25) ---

export const TAB_EDIT_KIND_LABELS: Record<TabEditKind, string> = {
  DETAILS: 'مشخصات',
  DIFFICULTY: 'سطح سختی',
  LYRICS: 'متن آهنگ',
  SCORE: 'نت‌ها'
};

export const TAB_EDIT_STATUS_LABELS: Record<TabEditStatus, string> = {
  PENDING: 'در انتظار',
  APPROVED: 'تأییدشده',
  REJECTED: 'ردشده',
  WITHDRAWN: 'پس‌گرفته‌شده'
};

export const LOCAL_TAB_EDIT_STATUS_LABELS: Record<LocalTabEditStatus, string> = {
  ...TAB_EDIT_STATUS_LABELS,
  QUEUED: 'در صف ارسال'
};

// --- Spec 6.5 Reject Presets ---

export interface EditRejectPreset {
  key: string;
  label: string;
  text: string;
}

export const EDIT_REJECT_PRESETS: readonly EditRejectPreset[] = [
  { key: 'incorrect', label: 'نادرست', text: 'تغییر پیشنهادی با اجرای اصلی قطعه مطابقت ندارد.' },
  { key: 'superseded', label: 'جایگزین شده', text: 'پیشنهاد دیگری برای همین مورد پیش‌تر تأیید شده است.' },
  { key: 'conflict', label: 'ناسازگار با نسخهٔ فعلی', text: 'تب پس از ارسال این پیشنهاد تغییر کرده است. لطفاً دوباره پیشنهاد دهید.' },
  { key: 'incomplete', label: 'ناقص', text: 'تغییر ناقص است یا بخشی از قطعه را خراب می‌کند.' },
  { key: 'wrong_metadata', label: 'مشخصات نادرست', text: 'نام قطعه، هنرمند یا کوک پیشنهادی درست نیست.' },
  { key: 'spam', label: 'نامرتبط', text: 'این پیشنهاد به محتوای تب مربوط نیست.' }
];

// --- Copy catalogue (Spec 7.1 C-01 .. C-72 & Section 4) ---

export const TAB_EDIT_COPY = {
  // C-01 .. C-09
  c01: 'تغییرات ذخیره شد.',
  c02: 'تغییرات اعمال شد.',
  c03: 'پیشنهاد شما ثبت شد و پس از تأیید مدیر اعمال می‌شود.',
  c04: 'پیشنهاد شما ذخیره شد و پس از اتصال ارسال می‌شود.',
  c04b: 'تغییرات ذخیره شد و پس از اتصال همگام می‌شود.',
  c05: 'دستگاه در حالت آفلاین است.',
  c06: 'پیشنهاد پس گرفته شد.',
  c06Action: 'بازگردانی',
  c07: 'این پیشنهاد قبلاً بررسی یا پس گرفته شده است.',
  c08: (title: string) => `پیشنهاد شما برای «${title}» پذیرفته نشد.`,
  c08b: 'ارسال انجام نشد. دوباره تلاش کنید.',
  c09: 'این تب دیگر در دسترس نیست؛ پیشنهاد شما حذف شد.',

  // C-10 .. C-17
  c10: 'تغییر شما پس از تأیید مدیر برای همه نمایش داده می‌شود. تا آن زمان نسخهٔ فعلی باقی می‌ماند.',
  c11Label: 'یادداشت برای مدیر (اختیاری)',
  c11Placeholder: 'مثلاً: منبع یا دلیل این تغییر',
  c12: 'شما یک پیشنهاد در انتظار برای این بخش دارید. ارسال دوباره جایگزین آن می‌شود.',
  c13: 'تغییری برای ارسال وجود ندارد.',
  c14: 'ویرایش مشخصات تب',
  c15Details: 'مشخصات',
  c15Difficulty: 'سطح سختی',
  c16Title: 'نام قطعه',
  c16Artist: 'هنرمند',
  c16Tuning: 'کوک',
  c17Suggest: 'ارسال پیشنهاد',
  c17Direct: 'ذخیره',
  c17Cancel: 'انصراف',
  c17Close: 'بستن',

  // C-20 .. C-25
  c20: 'برای پیشنهاد ویرایش وارد حساب کاربری شوید.',
  c20Action: 'ورود',
  c21: 'برای ثبت پیشنهاد، ابتدا ایمیل خود را تأیید کنید.',
  c21Action: 'تأیید ایمیل',
  c22: 'در انتظار تأیید',
  c23: 'در صف ارسال',
  c24Pending: 'در انتظار',
  c24Approved: 'تأییدشده',
  c24Rejected: 'ردشده',
  c24Withdrawn: 'پس‌گرفته‌شده',
  c25Details: 'مشخصات',
  c25Difficulty: 'سطح سختی',
  c25Lyrics: 'متن آهنگ',
  c25Score: 'نت‌ها',

  // C-30 .. C-35
  c30Title: 'برای این تب هنوز متن آهنگی ثبت نشده است.',
  c30Description: 'متن آهنگ را با آکوردها بنویسید و هر خط را به یک میزان وصل کنید تا همراه با پخش دنبال شود.',
  c30Action: 'افزودن متن آهنگ',
  c30bDescription: 'برای افزودن متن، وارد حساب کاربری شوید.',
  c31Title: 'متن آهنگ این تب حذف شود؟',
  c31Confirm: 'حذف',
  c31Cancel: 'انصراف',
  c31SuggestNote: 'این حذف پس از تأیید مدیر انجام می‌شود.',
  c32Title: 'تغییرات ذخیره‌نشده از بین می‌رود. بستن ویرایشگر؟',
  c32Confirm: 'بستن',
  c32Cancel: 'ادامهٔ ویرایش',
  c33: 'فایل متنی خوانده نشد. یک فایل .txt با کدگذاری UTF-8 انتخاب کنید.',
  c34Header: 'متن آهنگ (اختیاری)',
  c34Button: 'وارد کردن از فایل .txt',
  c34Counter: (n: number) => `${toPersianDigits(n)} از ۵۰٬۰۰۰`,
  c35Heading: 'یادداشت‌های من',
  c35Helper: 'این متن‌ها فقط برای شما نمایش داده می‌شوند و دیگر قابل ویرایش نیستند.',

  // C-40 .. C-47
  c40Title: 'حالت ویرایش نت‌ها',
  c40Undo: 'واگرد',
  c40Redo: 'ازنو',
  c40DeleteNote: 'حذف نت',
  c40AddNote: 'افزودن نت',
  c40ChordName: 'نام آکورد',
  c40DeleteChord: 'حذف آکورد',
  c40Tempo: 'تمپو',
  c40PlayBar: 'پخش این میزان',
  c40Cancel: 'انصراف',
  c41Hint: 'روی یک نت بزنید تا فرت آن را تغییر دهید، یا روی جای خالی یک سیم بزنید تا نت اضافه کنید.',
  c42Title: (n: number) => `${toPersianDigits(n)} تغییر ذخیره‌نشده دارید. از حالت ویرایش خارج می‌شوید؟`,
  c42Confirm: 'خروج',
  c42Cancel: 'ادامهٔ ویرایش',
  c43Title: (n: number) => `ادامهٔ ویرایش قبلی؟ (${toPersianDigits(n)} تغییر)`,
  c43Continue: 'ادامه',
  c43Restart: 'شروع دوباره',
  c44Title: (k: number) =>
    `این تب پس از شروع ویرایش شما به‌روز شده است. ${toPersianDigits(k)} تغییر شما دیگر قابل اعمال نیست و حذف می‌شود.`,
  c44Confirm: 'متوجه شدم',
  c44Restart: 'شروع دوباره',
  c45: 'این نت ادامهٔ نت قبلی است؛ نت اصلی انتخاب شد.',
  c46Title: 'ارسال تغییرات نت‌ها',
  c46Summary: (n: number, m: number) => `${toPersianDigits(n)} تغییر در ${toPersianDigits(m)} میزان`,
  c47Uploading: 'پس از کامل شدن بارگذاری می‌توانید نت‌ها را ویرایش کنید.',
  c47Guest: 'برای ویرایش نت‌ها وارد حساب کاربری شوید.',

  // C-50 .. C-53
  c50: 'پیش‌نمایش پیشنهاد شما؛ دیگران هنوز نسخهٔ فعلی را می‌بینند.',
  c50Action: 'بازگشت به نسخهٔ تأییدشده',
  c51: 'پیش‌نمایش پیشنهاد شما؛ دیگران هنوز نسخهٔ فعلی را می‌بینند.',
  c51Action: 'بازگشت به نسخهٔ تأییدشده',
  c52: (kind: string, reason: string) => `پیشنهاد شما (${kind}) برای این تب رد شد. دلیل: ${reason}`,
  c52Action: 'پیشنهادهای من',
  c53: 'نسخهٔ جدیدی از این تب موجود است.',
  c53Action: 'بارگذاری دوباره',

  // C-60 .. C-67
  c60: 'ویرایش تأیید و اعمال شد.',
  c61: 'این پیشنهاد بر پایهٔ نسخهٔ قدیمی‌تر تب است. با تأیید آن، تغییرات تأییدشدهٔ بعدی در همین بخش بازنویسی می‌شود.',
  c62: 'برخی تغییرات این پیشنهاد با نسخهٔ فعلی تب ناسازگار است و قابل اعمال نیست. آن را رد کنید تا نویسنده دوباره پیشنهاد دهد.',
  c63: 'این ویرایش با نسخهٔ فعلی تب سازگار نیست و اعمال نشد.',
  c64: 'پیشنهاد رد شد.',
  c65Stale: 'نسخهٔ قدیمی',
  c65Conflict: 'ناسازگار',
  c65HasLyrics: 'متن آهنگ دارد',
  c66Title: 'صف ویرایش‌ها خالی است',
  c66Description: 'پیشنهاد ویرایشی برای بررسی وجود ندارد.',
  c67Title: 'بارگذاری ویرایش‌ها انجام نشد',
  c67Description: 'اتصال خود را بررسی کنید و دوباره تلاش کنید.',
  c67Retry: 'تلاش دوباره',

  // C-70 .. C-72
  c70Title: 'پیشنهادهای من',
  c70EmptyTitle: 'هنوز پیشنهاد ویرایشی ثبت نکرده‌اید.',
  c70EmptyDescription: 'در صفحهٔ هر تب از منوی «...» گزینهٔ «ویرایش مشخصات» را بزنید.',
  c71ViewTab: 'مشاهدهٔ تب',
  c71Withdraw: 'پس گرفتن پیشنهاد',
  c71Edit: 'ویرایش پیشنهاد',
  c72RejectLine: (reason: string) => `دلیل رد: ${reason}`,

  // Section 4 limits copy
  limitTitleRequired: 'نام قطعه را وارد کنید.',
  limitTitleMax: 'نام قطعه نباید بیشتر از ۲۰۰ نویسه باشد.',
  limitArtistRequired: 'نام هنرمند را وارد کنید.',
  limitArtistMax: 'نام هنرمند نباید بیشتر از ۲۰۰ نویسه باشد.',
  limitTuningInvalid: 'کوک انتخاب‌شده معتبر نیست.',
  limitLyricsMax: 'متن آهنگ نباید بیشتر از ۵۰٬۰۰۰ نویسه باشد.',
  limitNoteMax: 'یادداشت نباید بیشتر از ۳۰۰ نویسه باشد.',
  limitRejectReasonRequired: 'دلیل رد را بنویسید.',
  limitScoreOpsMax: 'حداکثر ۲۰۰ تغییر در هر ارسال مجاز است.',
  limitChordInvalid: 'نام آکورد معتبر نیست.',
  limitTempoInvalid: 'تمپو باید بین ۲۰ تا ۴۰۰ باشد.',
  rateLimitWindow: (mins: number) =>
    `تعداد پیشنهادهای شما از حد مجاز گذشته است. ${toPersianDigits(mins)} دقیقه دیگر دوباره تلاش کنید.`,
  rateLimitPending: (n: number) =>
    `شما ${toPersianDigits(n)} پیشنهاد در انتظار بررسی دارید. تا بررسی آن‌ها صبر کنید یا برخی را پس بگیرید.`
} as const;

// --- Permission Helper (ADR 10.5, Spec 2.1 & 2.2) ---

export type CanEditTabMode = 'direct' | 'suggest' | 'none';
export type CanEditTabReason =
  | 'local'
  | 'owner-private'
  | 'admin'
  | 'suggest'
  | 'guest'
  | 'unverified'
  | 'flag-off'
  | 'uploading'
  | 'none';

export interface CanEditTabResult {
  mode: CanEditTabMode;
  reason: CanEditTabReason;
}

export interface TabViewerUser {
  id?: string;
  role?: UserRole;
  isGuest: boolean;
  emailVerified?: boolean;
}

export interface TabEditFlags {
  tabEditor: boolean;
}

/**
 * Checks whether the viewer can edit or suggest an edit for a tab.
 *
 * Evaluation order (ADR 10.5):
 * 1. SCORE and flag off -> none/flag-off
 * 2. local-only -> SCORE: none/uploading (queued upload) or none/guest, others direct/local
 * 3. guest -> none/guest
 * 4. owner + PRIVATE -> direct/owner-private
 * 5. admin + PENDING/APPROVED/REJECTED -> direct/admin
 * 6. tab viewable (APPROVED, or own) -> unverified ? none/unverified : suggest/suggest
 * 7. else none/none
 */
export function canEditTab(
  tab: Pick<TabSong, 'status' | 'uploaderId' | 'fileUrl' | 'pendingUpload'>,
  viewer: TabViewerUser,
  kind: TabEditKind,
  flags: TabEditFlags
): CanEditTabResult {
  // 1. SCORE and flag off -> none/flag-off
  if (kind === 'SCORE' && !flags.tabEditor) {
    return { mode: 'none', reason: 'flag-off' };
  }

  // 2. local-only -> SCORE: none/uploading (queued upload) or none/guest, others direct/local
  if (isLocalOnlyTab(tab)) {
    if (kind === 'SCORE') {
      if (tab.pendingUpload === 1) {
        return { mode: 'none', reason: 'uploading' };
      }
      return { mode: 'none', reason: 'guest' };
    }
    return { mode: 'direct', reason: 'local' };
  }

  // 3. guest -> none/guest
  if (viewer.isGuest || !viewer.id) {
    return { mode: 'none', reason: 'guest' };
  }

  const isOwner = Boolean(tab.uploaderId && tab.uploaderId === viewer.id);
  const isAdmin = viewer.role === 'ADMIN';

  // 4. owner + PRIVATE -> direct/owner-private
  if (isOwner && tab.status === 'PRIVATE') {
    return { mode: 'direct', reason: 'owner-private' };
  }

  // 5. admin + PENDING/APPROVED/REJECTED -> direct/admin
  if (isAdmin && (tab.status === 'PENDING' || tab.status === 'APPROVED' || tab.status === 'REJECTED')) {
    return { mode: 'direct', reason: 'admin' };
  }

  // 6. tab viewable (APPROVED, or own) -> unverified ? none/unverified : suggest/suggest
  const isViewable = tab.status === 'APPROVED' || isOwner;
  if (isViewable) {
    if (viewer.emailVerified === false) {
      return { mode: 'none', reason: 'unverified' };
    }
    return { mode: 'suggest', reason: 'suggest' };
  }

  // 7. else none/none
  return { mode: 'none', reason: 'none' };
}

// --- Lyrics Normalisation (ADR 6.2) ---

/**
 * Normalises lyrics identically to the API:
 * 1. CRLF/CR -> LF
 * 2. Strip control characters except \n (\u000A) and \t (\u0009)
 * 3. Strip trailing whitespace on each line
 * 4. Trim trailing blank lines
 *
 * Measured in UTF-16 code units (JS .length).
 */
export function normalizeLyrics(raw: string): string {
  if (!raw) return '';
  // 1. CRLF/CR -> LF
  let s = raw.replace(/\r\n?/g, '\n');
  // 2. Strip control chars except \n and \t
  // eslint-disable-next-line no-control-regex
  s = s.replace(/[\u0000-\u0008\u000B\u000C\u000E-\u001F\u007F]/g, '');
  // 3. Strip trailing whitespace on each line
  const lines = s.split('\n').map((line) => line.replace(/[^\S\n]+$/, ''));
  // 4. Trim trailing blank lines
  while (lines.length > 0 && lines[lines.length - 1] === '') {
    lines.pop();
  }
  return lines.join('\n');
}

// --- Error Mapping (Spec 6.6) ---

/**
 * Maps tab-edit API errors or error codes to Persian user-facing copy.
 */
export function mapTabEditError(err: unknown): string {
  if (!err) return TAB_EDIT_COPY.c08b;

  // If a string code was passed directly
  if (typeof err === 'string') {
    switch (err) {
      case 'EDIT_NO_CHANGE':
        return TAB_EDIT_COPY.c13;
      case 'EMAIL_NOT_VERIFIED':
        return TAB_EDIT_COPY.c21;
      case 'EDIT_NOT_PENDING':
        return TAB_EDIT_COPY.c07;
      case 'EDIT_CONFLICT':
        return TAB_EDIT_COPY.c63;
      case 'TOO_MANY_PENDING':
        return TAB_EDIT_COPY.rateLimitPending(EDIT_PENDING_MAX);
      case 'RATE_LIMITED':
        return TAB_EDIT_COPY.rateLimitWindow(60);
      case 'TAB_OPS_LIMIT':
      case 'FEATURE_DISABLED':
      case 'NETWORK_ERROR':
        return TAB_EDIT_COPY.c08b;
      default:
        return err;
    }
  }

  const anyErr = err as Record<string, unknown>;
  const data = (anyErr.data ?? anyErr) as Record<string, unknown>;
  const code = (data.code ?? anyErr.code) as string | undefined;
  const status = (anyErr.status ?? data.status) as number | undefined;

  if (code === 'EDIT_NO_CHANGE') return TAB_EDIT_COPY.c13;
  if (code === 'EMAIL_NOT_VERIFIED') return TAB_EDIT_COPY.c21;
  if (code === 'EDIT_NOT_PENDING') return TAB_EDIT_COPY.c07;
  if (code === 'EDIT_CONFLICT') return TAB_EDIT_COPY.c63;
  if (code === 'TOO_MANY_PENDING') {
    const pending = typeof data.pending === 'number' ? data.pending : EDIT_PENDING_MAX;
    return TAB_EDIT_COPY.rateLimitPending(pending);
  }
  if (code === 'RATE_LIMITED') {
    const retryAfter = typeof data.retryAfter === 'number' ? data.retryAfter : 3600;
    const mins = Math.max(1, Math.ceil(retryAfter / 60));
    return TAB_EDIT_COPY.rateLimitWindow(mins);
  }
  if (code === 'TAB_OPS_LIMIT' || code === 'FEATURE_DISABLED') {
    return TAB_EDIT_COPY.c08b;
  }

  if (status === 404) return TAB_EDIT_COPY.c09;
  if (status === 429) {
    const retryAfter = typeof data.retryAfter === 'number' ? data.retryAfter : 3600;
    const mins = Math.max(1, Math.ceil(retryAfter / 60));
    return TAB_EDIT_COPY.rateLimitWindow(mins);
  }
  if (status === 400 && code === 'INVALID_EDIT' && typeof data.message === 'string') {
    return data.message;
  }

  if (typeof data.message === 'string' && data.message) {
    return data.message;
  }
  if (typeof anyErr.message === 'string' && anyErr.message) {
    return anyErr.message;
  }

  return TAB_EDIT_COPY.c08b;
}
